const KEY = "pendingRedirect";
/** Сколько живёт сохранённое намерение возврата. */
export const REDIRECT_INTENT_TTL_MS = 24 * 60 * 60 * 1000;

/** Разрешаем только внутренние пути, чтобы не было open-redirect. */
function isSafePath(v: string | null | undefined): v is string {
  return !!v && v.startsWith("/") && !v.startsWith("//");
}

/** Разбор сохранённого значения: только JSON {path, at} моложе TTL; старая строка без времени — просрочена. */
export function parseStoredIntent(raw: string | null, now: number): string | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (!v || typeof v.path !== "string" || typeof v.at !== "number") return null;
    if (!isSafePath(v.path) || now - v.at > REDIRECT_INTENT_TTL_MS || v.at > now + 60_000)
      return null;
    return v.path;
  } catch {
    return null;
  }
}

export const serializeIntent = (path: string, now: number) => JSON.stringify({ path, at: now });

/**
 * Пригласительные ссылки, которые переживают регистрацию и забираются финалом мастера:
 * круг, конкретное событие, пост «Работы», запрос, профиль человека/эксперта, объявление,
 * кофе-группа, а также любые ссылки с ?ref=.
 */
export function isInviteIntent(path: string | null | undefined): boolean {
  if (!isSafePath(path)) return false;
  let url: URL;
  try {
    url = new URL(path, "https://x.local");
  } catch {
    return false;
  }
  const p = url.pathname;
  const q = url.searchParams;
  if (q.has("ref")) return true;
  if (p === "/arena" || p.startsWith("/arena/")) return true;
  if (/^\/(circles|events|people|marketplace)\/[^/]+/.test(p) && !p.startsWith("/events/new"))
    return true;
  if (/^\/coffee\/group\/[^/]+/.test(p)) return true;
  if (p === "/events" && q.has("event")) return true;
  if (p === "/work" && q.has("post")) return true;
  if (p === "/requests" && q.has("request")) return true;
  return false;
}

/** Финал мастера: свежий пригласительный адрес, иначе запасной путь. */
export function wizardDestination(intent: string | null, fallback: string): string {
  if (!intent || !isInviteIntent(intent)) return fallback;
  // Пришёл из «Свои на ARENA» — финал мастера ведёт сразу в раздел участников.
  if (isArenaIntent(intent)) return "/arena/people";
  return intent;
}

/** Намерение ведёт в раздел ARENA. */
export function isArenaIntent(path: string | null | undefined): boolean {
  if (!isSafePath(path)) return false;
  const p = path.split(/[?#]/)[0].replace(/\/+$/, "");
  return p === "/arena" || p.startsWith("/arena/");
}

/** Считать намерение возврата из ?redirect= или localStorage; просроченное стирается. */
export function getRedirectIntent(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const now = Date.now();
    const fromUrl = new URL(window.location.href).searchParams.get("redirect");
    if (isSafePath(fromUrl)) {
      window.localStorage.setItem(KEY, serializeIntent(fromUrl, now));
      return fromUrl;
    }
    const raw = window.localStorage.getItem(KEY);
    const path = parseStoredIntent(raw, now);
    if (!path && raw) window.localStorage.removeItem(KEY);
    return path;
  } catch {
    return null;
  }
}

export function setRedirectIntent(path: string): void {
  if (typeof window === "undefined" || !isSafePath(path)) return;
  try {
    window.localStorage.setItem(KEY, serializeIntent(path, Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearRedirectIntent(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Забрать намерение и очистить его. */
export function consumeRedirectIntent(): string | null {
  const v = getRedirectIntent();
  clearRedirectIntent();
  return v;
}
